import * as Components from '../components'
import * as Form from '../form'
import * as Hooks from '../hooks'
import * as Navigation from '../navigation'
import * as Stores from '../stores'
import { SizableText, View } from 'tamagui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import { router } from 'expo-router'
import { useGetUserInformation } from '@/src/common/hooks/useGetUserInformation'
import { parsePhoneNumber } from 'libphonenumber-js'
import { Mail } from '@/src/assets/icons/Mail'
import { mesaColorNames } from '@/src/common/theme'
import { UnitedStatesIcon } from '@/src/assets/icons/flags/UnitedStatesIcon'
import { Routes as AppRoutes } from '@/src/common/enums/routes'

const GetStartedFormSchema = Form.CardApplicationFormSchema.pick({
  firstName: true,
  lastName: true,
  phoneNumber: true,
  email: true,
  dob: true,
})
type GetStartedFormValues = z.infer<typeof GetStartedFormSchema>
const DefaultValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  dob: undefined,
} satisfies GetStartedFormValues

const formatPhoneNumber = (phoneNumber: string | undefined | null) => {
  if (!phoneNumber) {
    return ''
  }
  return parsePhoneNumber(phoneNumber, {
    defaultCountry: 'US',
  }).formatNational()
}

const containsValues = (values: GetStartedFormValues) =>
  values.firstName && values.lastName && values.phoneNumber && values.email && values.dob

const coalesceDefaults = (userInfo: ReturnType<typeof useGetUserInformation>['user']) => ({
  ...DefaultValues,
  ...(userInfo && {
    firstName: userInfo.firstName ?? '',
    lastName: userInfo.lastName ?? '',
    phoneNumber: formatPhoneNumber(userInfo.phoneNumber),
    email: userInfo.email,
  }),
})

export function GetStartedScreen() {
  const cardApplicationForm = Stores.useCardApplicationFormStore((s) => s.cardApplicationForm)
  const updateField = Stores.useCardApplicationFormStore((s) => s.updateField)
  const { user } = useGetUserInformation()
  const defaultValues = containsValues(cardApplicationForm)
    ? cardApplicationForm
    : coalesceDefaults(user)

  const form = useForm<GetStartedFormValues>({
    resolver: zodResolver(
      GetStartedFormSchema.refine(({ dob }) => !!dob, {
        path: ['dob'],
        message: 'Date of birth is required',
      }),
    ),
    defaultValues,
  })
  const { registerNav } = Hooks.useFormNav(form, ['firstName', 'lastName', 'dob'])

  const onSubmit = form.handleSubmit((data) => {
    // This is gross, but we don't have a good non-null non-undefined default
    // value for dates.
    if (!data.dob) {
      return
    }

    updateField('firstName', data.firstName)
    updateField('lastName', data.lastName)
    updateField('dob', data.dob)
    updateField('phoneNumber', data.phoneNumber)
    updateField('email', data.email)
    router.push(Navigation.Routes.OfferCode.route)
  })

  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Header previousRoute={AppRoutes.Main} />
        <Components.ApplicationScreen.Content title="Let's get started">
          <Components.FormHelpText>
            Confirm your information and tell us a little more about yourself
          </Components.FormHelpText>
          <Components.FormInput
            control={form.control}
            name="firstName"
            {...Form.FormInputProps.firstName}
            {...registerNav('firstName')}
          />
          <Components.FormInput
            control={form.control}
            name="lastName"
            {...Form.FormInputProps.lastName}
            {...registerNav('lastName')}
          />
          <Components.FormInputDateOfBirth
            control={form.control}
            name="dob"
            {...Form.FormInputProps.dob}
            {...registerNav('dob')}
          />
          <Components.FormInput
            control={form.control}
            name="phoneNumber"
            {...Form.FormInputProps.phoneNumber}
            leftIcon={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <UnitedStatesIcon />
                <SizableText>+1</SizableText>
              </View>
            }
          />
          <Components.FormInput
            control={form.control}
            name="email"
            {...Form.FormInputProps.email}
            leftIcon={<Mail stroke={mesaColorNames['Greyscale/400 #A4AAAC']} />}
          />
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void onSubmit()}
          disabled={form.formState.isSubmitted && !form.formState.isValid}
          loading={form.formState.isSubmitting}
          title="Continue"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
